import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { gql, useLazyQuery } from "@apollo/client";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useParams } from "react-router-dom";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Select,
  Grid,
  Button,
  Autocomplete,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { IUser } from "../../../types/user";
import { IProjectAccess } from "../../../types/user";
import { IProject } from "../../../types/project";
import ReportPhoneTables from "../../project/report/ReportCallTable";
import ReportProjectTables from "./ReportProjectTables";
import { useAppDispatch } from "../../../app/hooks";
import { showErrorSnackbar, showSuccessSnackbar } from "../../../features/snackbar/snackbarSlice";
import { useSalesQuery } from "../../../features/user/userHooks";
import { selectUsers } from "../../../features/user/userSlice";

const Reporting = (props: ChildProps) => {
  const { user, global, selectedProject, filter } = props;
  const users = useSelector(selectUsers);
  const { projectid } = useParams();
  const storeDispatch = useAppDispatch();

  const [project, setProject] = useState<IProject | null>(selectedProject ? selectedProject : null);
  const [dateStart, setDateStart] = useState<any>(new Date(new Date().setHours(0, 0, 0, 0)));
  const [dateEnd, setDateEnd] = useState<any>(new Date(new Date().setHours(23, 59, 59, 999)));
  const [projectTotals, setProjectTotals] = useState<IProjectTotal[][]>([]);
  const [phoneTotals, setPhoneTotals] = useState<IPhoneTotal[][]>([]);
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [show, setShow] = useState<boolean>(false);
  useSalesQuery("Admin");

  const [getUserStats, { loading }] = useLazyQuery(GETUSERSTATS, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setProjectTotals(data.userStats.projects);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar("Error getting stats"));
    },
  });

  const [downloadRdsReport, { loading: reportLoading }] = useLazyQuery(RDSDEBRIEF, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar("Email has been sent!"));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar("Error getting stats"));
    },
  });

  const [getCallStats, { loading: callLoading }] = useLazyQuery(GETCALLSTATS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setPhoneTotals(data.callStats);
    },
    onError: (err) => {
      setPhoneTotals([]);
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  useEffect(() => {
    if (!global) {
      setSelectedUsers([user]);
    }
    if (projectid) {
      setProject(selectedProject!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectid]);

  useEffect(() => {
    if (dateStart && dateEnd && dateStart.toString() !== "Invalid Date" && dateEnd.toString() !== "Invalid Date") {
      if (selectedProject) {
        getUserStats({
          variables: {
            user:
              user?.type === "Manager"
                ? selectedUsers.length
                  ? [...selectedUsers.map((user: any) => user._id)]
                  : []
                : [...selectedUsers.map((user: any) => user._id)],
            project: selectedProject._id,
            dateStart,
            dateEnd,
            userFilter: "All",
            debrief: false,
          },
        });
      } else {
        getUserStats({
          variables: {
            user:
              user?.type === "Manager"
                ? selectedUsers.length
                  ? [...selectedUsers.map((user: any) => user._id)]
                  : []
                : [...selectedUsers.map((user: any) => user._id)],
            project: project ? project._id : null,
            dateStart,
            dateEnd,
            userFilter: "",
            debrief: false,
          },
        });
        setPhoneTotals([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStart, dateEnd, project, getUserStats, selectedUsers, filter, selectedProject]);

  const handleProject = (e: any) => {
    setShow(false);
    let selectedProject = user?.projectAccess.find((projectAccess: IProjectAccess) => projectAccess.project._id === e);
    if (selectedProject) {
      setProject(selectedProject.project);
    } else {
      setProject(null);
    }
  };

  const handleUser = (e: any, value: any) => {
    setShow(false);
    setProject(null);
    if (value) {
      setSelectedUsers(value);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleTwilio = async (username: any) => {
    let selectedUser = selectedUsers.find((user: IUser) => user.fullName === username[0].user);
    setShow(true);
    if (selectedProject && user?.type !== "Developer") {
      await getCallStats({
        variables: {
          user: user?.type === "Manager" ? (selectedUser ? selectedUser._id : user?._id) : user?._id,
          project: selectedProject._id,
          dateStart,
          dateEnd,
          userFilter: "All",
        },
      });
    }
    if (!selectedProject && user?.type !== "Developer") {
      await getCallStats({
        variables: {
          user: user?.type === "Manager" ? (selectedUser ? selectedUser._id : null) : user?._id,
          project: project ? project._id : null,
          dateStart,
          dateEnd,
          userFilter: "",
        },
      });
    }
  };

  return (
    <Box>
      {filter ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={"Start Date (MM/DD/YYYY)"}
                value={dateStart}
                onChange={(newValue) => {
                  if (new Date(newValue).valueOf() > new Date(dateEnd).valueOf()) {
                    return storeDispatch(showErrorSnackbar("Start Date cannot be greater than End Date"));
                  }
                  setShow(false);
                  setDateStart(newValue);
                }}
                sx={{ width: "100%" }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={"End Date (YYYY/MM/DD)"}
                value={dateEnd}
                onChange={(newValue) => {
                  if (new Date(dateStart).valueOf() > new Date(newValue).valueOf()) {
                    return storeDispatch(showErrorSnackbar("Start Date cannot be greater than End Date"));
                  }
                  setShow(false);
                  setDateEnd(newValue);
                }}
                sx={{ width: "100%" }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item sm={3} sx={{ display: "dlex" }}>
            <Box sx={{ mr: 2 }}>
              <Button onClick={() => setDateStart(new Date("January 1, 2023 00:00:00"))} variant="contained">
                Overall
              </Button>
            </Box>
            {user.type === "Manager" ? (
              <Box>
                <Button
                  color="primary"
                  disabled={reportLoading}
                  onClick={() => downloadRdsReport({ variables: { dateStart, dateEnd, email: true } })}
                  variant="contained"
                >
                  {reportLoading ? <CircularProgress size={16} /> : "Sales Debrief Report"}
                </Button>
              </Box>
            ) : null}
          </Grid>
          {!projectid ? (
            <Grid item sm={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel id="demo-simple-select-project">Project</InputLabel>
                <Select
                  label="Project"
                  sx={{ width: "100%" }}
                  fullWidth
                  labelId="demo-simple-select-project"
                  id="demo-simple-project"
                  value={project ? project._id : "All"}
                  onChange={(e: any) => {
                    handleProject(e.target.value);
                  }}
                  required
                >
                  <MenuItem disabled={!project} value="All">
                    All
                  </MenuItem>
                  {user?.projectAccess.map((projectAccess: IProjectAccess, index: number) => {
                    return (
                      <MenuItem key={index} value={projectAccess.project._id}>
                        {projectAccess.project.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          ) : null}
          {!projectid && user?.type === "Manager" ? (
            <Grid item sm={6}>
              <Autocomplete
                multiple
                id="users"
                options={users}
                freeSolo={false}
                getOptionLabel={(option: any) => option.fullName}
                isOptionEqualToValue={(option, value) => option === value}
                value={selectedUsers}
                onChange={(e, value) => handleUser(e, value)}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Users" />}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}
      {!loading ? (
        projectTotals.map((projectTotal: any, index: number) => {
          return (
            <Box key={index}>
              <Box>
                <ReportProjectTables
                  loading={loading}
                  user={user!}
                  title={projectTotal[0].user}
                  projectTotals={projectTotal}
                  dateStart={dateStart}
                  dateEnd={dateEnd}
                  project={project}
                  users={selectedUsers}
                />
              </Box>
              {callLoading ? (
                <>
                  <Skeleton height={100} animation="wave" />
                  <Skeleton height={400} sx={{ mt: -10 }} animation="wave" />
                </>
              ) : user?.type !== "Developer" && show && phoneTotals.length && phoneTotals[index].length ? (
                <Box>
                  <ReportPhoneTables
                    loading={callLoading}
                    user={user!}
                    phoneTotals={phoneTotals[index]}
                    dateStart={dateStart}
                    dateEnd={dateEnd}
                  />
                </Box>
              ) : (
                <Box
                  sx={{ textAlign: "center", mt: 2 }}
                  onClick={() => {
                    handleTwilio(projectTotal);
                  }}
                >
                  <Button variant="contained">View Call Report</Button>
                </Box>
              )}
            </Box>
          );
        })
      ) : (
        <>
          <Skeleton height={100} animation="wave" />
          <Skeleton height={400} sx={{ mt: -10 }} animation="wave" />
        </>
      )}
    </Box>
  );
};

interface ChildProps {
  user: IUser;
  global?: boolean;
  selectedProject?: IProject;
  filter?: boolean;
}

export interface IProjectTotal {
  name: string;
  user: string;
  processesAssigned: number;
  stepsCompleted: number;
  stepsOverDue: number;
  appointmentsCreated: number;
  appointmentsBooked: number;
  appointmentsSales: number;
  cesInteractions: number;
  emailsSent: number;
  handoverConnections: number;
  appointmentsShowedUp: number;
  notes: number;
}

export interface IPhoneTotal {
  user: IUser | null;
  project: IProject | null;
  name: string;
  outboundCalls: number;
  inboundCalls: number;
  missedCalls: number;
  averageDurationOutbound: string;
  averageDurationInbound: string;
  callsReturned: number | null;
}

export interface ICallsReturned {
  businessPhone: string;
  customerPhone: string;
  startDate: Date;
  callBackDate: Date;
}

const GETUSERSTATS = gql`
  query userStats($user: [MongoID], $project: MongoID, $dateStart: Date!, $dateEnd: Date!, $userFilter: String, $debrief: Boolean!) {
    userStats(user: $user, project: $project, dateStart: $dateStart, dateEnd: $dateEnd, userFilter: $userFilter, debrief: $debrief) {
      projects {
        id
        name
        user
        processesAssigned
        stepsCompleted
        stepsOverDue
        appointmentsCreated
        appointmentsBooked
        appointmentsSales
        cesInteractions
        handoverConnections
        appointmentsShowedUp
        emailsSent
        notes
      }
    }
  }
`;

const GETCALLSTATS = gql`
  query callStats($user: MongoID, $project: MongoID, $dateStart: Date!, $dateEnd: Date!, $userFilter: String) {
    callStats(user: $user, project: $project, dateStart: $dateStart, dateEnd: $dateEnd, userFilter: $userFilter) {
      user {
        _id
        fullName
      }
      outboundCalls
      inboundCalls
      missedCalls
      averageDurationOutbound
      averageDurationInbound
      callsReturned
    }
  }
`;

const RDSDEBRIEF = gql`
  query rdsDebriefReport($dateStart: Date!, $dateEnd: Date!, $email: Boolean!) {
    rdsDebriefReport(dateStart: $dateStart, dateEnd: $dateEnd, email: $email) {
      project
    }
  }
`;

export default Reporting;
